import { useMutation, useQueryClient, useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute, Outlet, redirect, useNavigate } from "@tanstack/react-router";
import { prefetchRememberedRegister } from "@/cashier/prefetch";
import { AppShell } from "@/components/app-shell";
import { CurrentOutletProvider } from "@/components/current-outlet";
import { api, isUnauthenticated, unwrap } from "@/lib/api";
import { brandQuery, meScopesQuery, outletsQuery } from "@/lib/queries";

/** Pathless layout guarding every signed-in screen, inside the app shell. */
export const Route = createFileRoute("/_authed")({
  beforeLoad: async ({ context, location }) => {
    // Asked together, not one after another: every signed-in screen needs all three, and none needs
    // another's answer to ask (PRD §5 cold start; scripts/perf-budgets.ts). Signed out, they fail with
    // the scopes and the redirect below handles it.
    void context.queryClient.prefetchQuery(outletsQuery);
    void context.queryClient.prefetchQuery(brandQuery);
    // The register's menu too, for the outlet it opened on last time (see prefetchRememberedRegister).
    if (location.pathname === "/cashier") prefetchRememberedRegister(context.queryClient);
    try {
      await context.queryClient.ensureQueryData(meScopesQuery);
    } catch (error) {
      if (isUnauthenticated(error)) throw redirect({ to: "/login", search: { redirect: location.href } });
      throw error;
    }
  },
  // The outlet switcher and page eyebrows need the (bounded) outlet list.
  loader: ({ context }) => context.queryClient.ensureQueryData(outletsQuery),
  component: AuthedLayout,
});

function AuthedLayout() {
  const { data: scopes } = useSuspenseQuery(meScopesQuery);
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const signOut = useMutation({
    mutationFn: async () => unwrap(await api.v1.auth.sessions.current.delete()),
    onSettled: async () => {
      queryClient.clear();
      await navigate({ to: "/login" });
    },
  });

  return (
    <CurrentOutletProvider>
      <AppShell session={{ scopes, signOut: () => signOut.mutate(), signingOut: signOut.isPending }}>
        <Outlet />
      </AppShell>
    </CurrentOutletProvider>
  );
}
